// client/src/components/admin/AdminMonitoring.js
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import useAuth from '../../hooks/useAuth'
import PageLayout from '../common/layout/PageLayout'
import * as monitoringApi from '../../api/monitoring'
import InfoTooltip from '../common/InfoTooltip'
import HelpModal from '../common/HelpModal'
import MonitoringHelpContent from './MonitoringHelpContent'
import './AdminMonitoring.css'

function AdminMonitoring() {
  const navigate = useNavigate()
  const { isAdmin, loading: authLoading } = useAuth()

  const [healthStatus, setHealthStatus] = useState(null)
  const [incidents, setIncidents] = useState([])
  const [metrics, setMetrics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const [showUnresolvedOnly, setShowUnresolvedOnly] = useState(true)

  // Load monitoring data
  const loadData = useCallback(async () => {
    try {
      setError(null)

      const [healthData, incidentsData, metricsData] = await Promise.all([
        monitoringApi.getHealthStatus(),
        monitoringApi.getIncidents({
          limit: 50,
          unresolvedOnly: showUnresolvedOnly,
        }),
        monitoringApi.getMetrics(),
      ])

      setHealthStatus(healthData)
      setIncidents(incidentsData.incidents || [])
      setMetrics(metricsData.metrics)
    } catch (err) {
      console.error('Error loading monitoring data:', err)
      setError('Failed to load monitoring data. Please try again.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [showUnresolvedOnly])

  useEffect(() => {
    if (!authLoading && isAdmin) {
      loadData()
    }
  }, [authLoading, isAdmin, loadData])

  // Refresh data
  const handleRefresh = useCallback(() => {
    setRefreshing(true)
    loadData()
  }, [loadData])

  // Collect metrics manually
  const handleCollectMetrics = async () => {
    try {
      setRefreshing(true)
      await monitoringApi.collectMetrics(5)
      await loadData()
    } catch (err) {
      console.error('Error collecting metrics:', err)
      setError('Failed to collect metrics')
      setRefreshing(false)
    }
  }

  // Evaluate thresholds manually
  const handleEvaluateThresholds = async () => {
    try {
      setRefreshing(true)
      await monitoringApi.evaluateThresholds()
      await loadData()
    } catch (err) {
      console.error('Error evaluating thresholds:', err)
      setError('Failed to evaluate thresholds')
      setRefreshing(false)
    }
  }

  // Navigate to digest page (to be created)
  const handleViewDigest = () => {
    navigate('/admin/monitoring/digest')
  }

  // Auth checks
  if (authLoading) {
    return (
      <PageLayout>
        <div className="loading-spinner">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!isAdmin) {
    return (
      <PageLayout>
        <div className="error-message">
          <h3>Access Denied</h3>
          <p>You must be an administrator to view this page.</p>
        </div>
      </PageLayout>
    )
  }

  if (loading && !healthStatus) {
    return (
      <PageLayout>
        <div className="loading-spinner">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading monitoring data...</span>
          </div>
        </div>
      </PageLayout>
    )
  }

  const currentStatus = healthStatus?.current_status || {}
  const overallStatus = currentStatus.overall_status || 'unknown'

  return (
    <PageLayout>
      <div className="admin-monitoring">
        <div className="monitoring-header">
          <h1>System Monitoring</h1>
          <div className="monitoring-actions">
            <button
              className={`btn btn-sm btn-outline-primary refresh-button ${refreshing ? 'refreshing' : ''}`}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <span className="refresh-icon">↻</span>
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={handleCollectMetrics}
              disabled={refreshing}
            >
              Collect Metrics
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={handleEvaluateThresholds}
              disabled={refreshing}
            >
              Evaluate Thresholds
            </button>
            <button
              className="btn btn-sm btn-outline-info"
              onClick={handleViewDigest}
            >
              View Digest
            </button>
            <button
              className="btn btn-sm btn-outline-primary"
              data-bs-toggle="modal"
              data-bs-target="#monitoringHelpModal"
              aria-label="Open monitoring help"
            >
              <span aria-hidden="true">?</span> Help
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Health Status Tiles */}
        <div className="health-tiles">
          <div className={`health-tile ${overallStatus}`}>
            <div className="tile-header">
              <h3 className="tile-title">
                Overall Health
                <InfoTooltip content="Aggregated system health status based on all monitored metrics. Healthy means all metrics are within target ranges." />
              </h3>
              <span className="tile-status-icon">
                {overallStatus === 'healthy' && '✅'}
                {overallStatus === 'degraded' && '⚠️'}
                {overallStatus === 'unhealthy' && '🔴'}
                {overallStatus === 'unknown' && '❓'}
              </span>
            </div>
            <div className="tile-value">{overallStatus.toUpperCase()}</div>
            {currentStatus.snapshot_time && (
              <div className="tile-label timestamp">
                Last updated:{' '}
                {new Date(currentStatus.snapshot_time).toLocaleString()}
              </div>
            )}
          </div>

          <div
            className={`health-tile ${currentStatus.database_status === 'ok' ? 'healthy' : currentStatus.database_status === 'slow' ? 'degraded' : 'unhealthy'}`}
          >
            <div className="tile-header">
              <h3 className="tile-title">
                Database
                <InfoTooltip content="Database connection pool usage. Target: <70%. Warning at >70%, critical at >90%." />
              </h3>
              <span className="tile-status-icon">
                {currentStatus.database_status === 'ok' && '✅'}
                {currentStatus.database_status === 'slow' && '⚠️'}
                {currentStatus.database_status === 'error' && '🔴'}
              </span>
            </div>
            <div className="tile-value">
              {currentStatus.db_pool_usage_percent != null
                ? Number(currentStatus.db_pool_usage_percent).toFixed(1)
                : 'N/A'}
              <span className="metric-unit">%</span>
            </div>
            <div className="tile-label">Pool Usage</div>
          </div>

          <div
            className={`health-tile ${currentStatus.api_status === 'ok' ? 'healthy' : currentStatus.api_status === 'degraded' ? 'degraded' : 'unhealthy'}`}
          >
            <div className="tile-header">
              <h3 className="tile-title">
                API
                <InfoTooltip content="Average API response time. Target: <500ms. Degraded at >500ms, critical at >1000ms." />
              </h3>
              <span className="tile-status-icon">
                {currentStatus.api_status === 'ok' && '✅'}
                {currentStatus.api_status === 'degraded' && '⚠️'}
                {currentStatus.api_status === 'error' && '🔴'}
              </span>
            </div>
            <div className="tile-value">
              {currentStatus.avg_response_time_ms != null
                ? Number(currentStatus.avg_response_time_ms).toFixed(0)
                : 'N/A'}
              <span className="metric-unit">ms</span>
            </div>
            <div className="tile-label">Avg Response Time</div>
          </div>

          <div
            className={`health-tile ${currentStatus.error_rate_percent < 1 ? 'healthy' : currentStatus.error_rate_percent < 5 ? 'degraded' : 'unhealthy'}`}
          >
            <div className="tile-header">
              <h3 className="tile-title">
                Error Rate
                <InfoTooltip content="Percentage of failed requests. Target: <1%. Warning at >1%, critical at >5%." />
              </h3>
              <span className="tile-status-icon">
                {Number(currentStatus.error_rate_percent || 0) < 1 && '✅'}
                {Number(currentStatus.error_rate_percent || 0) >= 1 &&
                  Number(currentStatus.error_rate_percent || 0) < 5 &&
                  '⚠️'}
                {Number(currentStatus.error_rate_percent || 0) >= 5 && '🔴'}
              </span>
            </div>
            <div className="tile-value">
              {currentStatus.error_rate_percent != null
                ? Number(currentStatus.error_rate_percent).toFixed(2)
                : 'N/A'}
              <span className="metric-unit">%</span>
            </div>
            <div className="tile-label">
              {currentStatus.requests_failed || 0} /{' '}
              {currentStatus.requests_total || 0} requests
            </div>
          </div>

          <div className="health-tile healthy">
            <div className="tile-header">
              <h3 className="tile-title">
                Uptime
                <InfoTooltip content="System availability percentage. SLA target: 99.9%. Degraded below 99.9%, critical below 99%." />
              </h3>
              <span className="tile-status-icon">
                {Number(currentStatus.uptime_percent || 100) >= 99.9 && '✅'}
                {Number(currentStatus.uptime_percent || 100) >= 99 &&
                  Number(currentStatus.uptime_percent || 100) < 99.9 &&
                  '⚠️'}
                {Number(currentStatus.uptime_percent || 100) < 99 && '🔴'}
              </span>
            </div>
            <div className="tile-value">
              {currentStatus.uptime_percent != null
                ? Number(currentStatus.uptime_percent).toFixed(2)
                : '100.00'}
              <span className="metric-unit">%</span>
            </div>
            <div className="tile-label">SLA Target: 99.9%</div>
          </div>

          <div className="health-tile">
            <div className="tile-header">
              <h3 className="tile-title">
                Incidents
                <InfoTooltip content="Number of unresolved incidents. Critical incidents require immediate attention, warnings should be monitored." />
              </h3>
              <span className="tile-status-icon">
                {healthStatus?.summary?.critical_unresolved > 0 && '🔴'}
                {healthStatus?.summary?.critical_unresolved === 0 &&
                  healthStatus?.summary?.warning_unresolved > 0 &&
                  '⚠️'}
                {healthStatus?.summary?.total_unresolved === 0 && '✅'}
              </span>
            </div>
            <div className="tile-value">
              {healthStatus?.summary?.total_unresolved || 0}
            </div>
            <div className="tile-label">
              {healthStatus?.summary?.critical_unresolved || 0} critical,{' '}
              {healthStatus?.summary?.warning_unresolved || 0} warnings
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        {metrics?.latest && (
          <div className="metrics-section">
            <h2>
              Performance Metrics
              <InfoTooltip content="Latency percentiles show how fast different portions of requests complete. Lower percentiles indicate typical performance." />
            </h2>
            <div className="metrics-grid">
              {metrics.latest.request_latency && (
                <>
                  <div className="metric-card">
                    <div className="metric-label">
                      P50 Latency
                      <InfoTooltip content="Median latency - 50% of requests complete faster than this time. Good indicator of typical user experience." />
                    </div>
                    <div className="metric-value">
                      {metrics.latest.request_latency.p50 != null
                        ? Number(metrics.latest.request_latency.p50).toFixed(2)
                        : 'N/A'}
                      <span className="metric-unit">ms</span>
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-label">
                      P95 Latency
                      <InfoTooltip content="95th percentile latency - 95% of requests complete faster than this. Shows worst-case performance for most users." />
                    </div>
                    <div className="metric-value">
                      {metrics.latest.request_latency.p95 != null
                        ? Number(metrics.latest.request_latency.p95).toFixed(2)
                        : 'N/A'}
                      <span className="metric-unit">ms</span>
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-label">
                      P99 Latency
                      <InfoTooltip content="99th percentile latency - only 1% of requests are slower than this. Indicates tail latency and outlier performance." />
                    </div>
                    <div className="metric-value">
                      {metrics.latest.request_latency.p99 != null
                        ? Number(metrics.latest.request_latency.p99).toFixed(2)
                        : 'N/A'}
                      <span className="metric-unit">ms</span>
                    </div>
                  </div>
                </>
              )}
              {metrics.latest.request_volume && (
                <div className="metric-card">
                  <div className="metric-label">
                    Request Volume
                    <InfoTooltip content="Total number of requests processed in the current time window." />
                  </div>
                  <div className="metric-value">
                    {metrics.latest.request_volume.dimensions?.total_requests ||
                      'N/A'}
                    <span className="metric-unit">req</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Incidents Table */}
        <div className="incidents-section">
          <div className="incidents-header">
            <h2>Recent Incidents</h2>
            <div className="incidents-filters">
              <label>
                <input
                  type="checkbox"
                  checked={showUnresolvedOnly}
                  onChange={(e) => setShowUnresolvedOnly(e.target.checked)}
                />{' '}
                Show unresolved only
              </label>
            </div>
          </div>

          {incidents.length === 0 ? (
            <div className="no-data">
              <p>✅ No incidents to display</p>
            </div>
          ) : (
            <table className="incidents-table">
              <thead>
                <tr>
                  <th>
                    Time
                    <InfoTooltip content="When the incident was first detected" />
                  </th>
                  <th>
                    Severity
                    <InfoTooltip content="Critical requires immediate attention, Warning should be monitored, Info is informational only" />
                  </th>
                  <th>
                    Type
                    <InfoTooltip content="Category of the incident (e.g., high_error_rate, slow_response)" />
                  </th>
                  <th>Message</th>
                  <th>
                    Metric Value
                    <InfoTooltip content="The metric value that triggered this incident" />
                  </th>
                  <th>
                    Status
                    <InfoTooltip content="Active incidents are ongoing, Resolved incidents have returned to normal" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {incidents.map((incident) => (
                  <tr key={incident.id}>
                    <td className="timestamp">
                      {new Date(incident.created_at).toLocaleString()}
                    </td>
                    <td>
                      <span className={`severity-badge ${incident.severity}`}>
                        {incident.severity.toUpperCase()}
                      </span>
                    </td>
                    <td className="incident-type">{incident.incident_type}</td>
                    <td className="incident-message">{incident.message}</td>
                    <td>
                      {incident.metric_value != null
                        ? Number(incident.metric_value).toFixed(2)
                        : 'N/A'}
                    </td>
                    <td>
                      {incident.resolved ? (
                        <span style={{ color: '#28a745' }}>✓ Resolved</span>
                      ) : (
                        <span style={{ color: '#dc3545' }}>⚠ Active</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Help Modal */}
        <HelpModal
          id="monitoringHelpModal"
          title="System Monitoring Help"
          size="lg"
        >
          <MonitoringHelpContent />
        </HelpModal>
      </div>
    </PageLayout>
  )
}

export default AdminMonitoring
